import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import { SharedImageMod, SharedImageModSchema } from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import {
  ImageMod,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

export const imageModHandler: ModHandler<ImageMod> = {
  normalize: (mod) => ({
    ...mod,
    selector: mod.selector.trim(),
    src: mod.src.trim(),
    imageSearchUrl: mod.imageSearchUrl?.trim() ?? null,
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit") {
      throw new Error("Expected onsiteEdit");
    }

    const edit = conversion.dto;
    const editMode = edit.onsiteEditType === "html" ? "html" : "image";
    const selector = edit.querySelectors.join(",");
    return {
      id: edit.id,
      type: "image",
      category: "basicMod",
      groupId: edit.groupId,
      variationId: edit.variationId,
      selector,
      description: edit.title ?? undefined,
      src: editMode === "image" ? edit.replace : "",
      remove: edit.remove,
      editMode: edit.onsiteEditType === "html" ? "html" : "image",
      html: editMode === "html" ? edit.replace : "",
      imageSearchUrl: edit.imageSearchUrl ?? null,
      nodes: [],
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
    };
  },

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      groupId: mod.groupId,
      variationId: mod.variationId,
      replace: mod.editMode === "html" ? mod.html : mod.src,
      find: "",
      querySelectors: [mod.selector],
      isRegex: false,
      skip: false,
      remove: mod.remove,
      isHtml: false,
      isImage: false,
      onsiteEditType: mod.editMode,
      imageSearchUrl: mod.imageSearchUrl,
      insertType: null,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    const content = mod.editMode === "html" ? mod.html : mod.src;
    if (!mod.selector && !mod.imageSearchUrl) {
      errors.selector = "Element selection or image URL is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    if (!content && !mod.remove) {
      if (mod.editMode === "html") {
        errors.html = "HTML or hide element is required";
        errors.remove = "HTML or hide element is required";
      } else {
        errors.src = "Image or hide element is required";
        errors.remove = "Image or hide element is required";
      }
    }
    return errors;
  },

  isEquivalent: (a, b) => b.type === "image" && a.selector === b.selector,

  defaults: () => ({
    id: v4(),
    type: "image",
    category: "basicMod",
    variationId: "",
    editMode: "image",
    selector: "",
    remove: false,
    src: "",
    html: "",
    imageSearchUrl: null,
    nodes: [],
  }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedImageMod>(SharedImageModSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? imageModHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const normalize = (mod: ImageMod) => ({
      description: mod.description,
      editMode: mod.editMode,
      selector: mod.selector,
      remove: mod.remove,
      src: mod.src,
      // Only compare html when editMode is "html" - for image mode, html is just cached DOM state
      ...(mod.editMode === "html" && { html: mod.html }),
      imageSearchUrl: mod.imageSearchUrl,
    });
    return !isEqual(normalize(oldMod), normalize(newMod));
  },
};
